import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ItineraryCard from "@/components/ItineraryCard";
import { api } from "@/lib/api";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "Asia", label: "Asia", region: true },
  { id: "Europe", label: "Europe", region: true },
  { id: "Americas", label: "Americas", region: true },
  { id: "Africa", label: "Africa", region: true },
  { id: "Solo", label: "Solo", trip: true },
  { id: "Couple", label: "Couple", trip: true },
  { id: "Family", label: "Family", trip: true },
  { id: "₹", label: "Budget (₹)", budget: "₹" },
  { id: "₹₹", label: "Mid-range (₹₹)", budget: "₹₹" },
  { id: "₹₹₹", label: "Luxury (₹₹₹)", budget: "₹₹₹" },
  { id: "free", label: "Free", free: true },
];

export default function Explore() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");

  const params = useMemo(() => {
    const f = FILTERS.find((x) => x.id === active) || {};
    const p = {};
    if (f.region) p.region = f.id;
    if (f.trip) p.trip_type = f.id;
    if (f.budget) p.budget = f.budget;
    if (f.free) p.free = true;
    if (search) p.q = search;
    return p;
  }, [active, search]);

  useEffect(() => {
    setLoading(true);
    api.get("/itineraries", { params }).then((r) => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [params]);

  return (
    <div data-testid="explore-page">
      <Navbar />

      <section className="px-6 sm:px-10 pt-12 pb-8 max-w-[1280px] mx-auto">
        <span className="eyebrow">◆ {items.length} routes · 47 countries</span>
        <h1 className="h-display mt-3 text-[44px] sm:text-[64px]">
          Where to <em>next?</em>
        </h1>

        <div
          className="mt-8 bg-[var(--card)] border-[1.5px] border-[var(--line)] rounded-full pl-5 pr-2 py-2 flex items-center gap-3 max-w-[720px] focus-within:border-[var(--ink)] transition-colors"
          data-testid="search-bar"
        >
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tokyo · Lisbon · Marrakech · Buenos Aires…"
            className="flex-1 bg-transparent outline-none text-[15px] py-2"
            data-testid="search-input"
          />
          <button
            onClick={() => setSearch(search)}
            data-testid="search-button"
            className="btn btn-solid btn-sm"
          >
            Search
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2.5" data-testid="filter-row">
          {FILTERS.map((f) => (
            <span
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setActive(f.id)}
              className={`pill ${active === f.id ? "on" : ""}`}
            >
              {f.label}
            </span>
          ))}
        </div>
      </section>

      <section className="px-6 sm:px-10 pb-20 max-w-[1280px] mx-auto">
        {loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card" style={{ height: 420 }}>
                <div className="card-img bg-[var(--line)]" />
                <div className="card-body">
                  <div className="h-3 w-24 bg-[var(--line)] rounded mb-3" />
                  <div className="h-6 w-full bg-[var(--line)] rounded mb-2" />
                  <div className="h-6 w-3/4 bg-[var(--line)] rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="empty-state"
            className="text-center py-20 border-[1.5px] border-dashed border-[var(--line)] rounded-[18px]"
          >
            <span className="eyebrow">◆ Nothing matched</span>
            <p className="mt-3 text-[var(--ink2)]">
              No routes match these filters. Try widening your search.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="itinerary-grid">
            {items.map((it) => (
              <ItineraryCard key={it.itinerary_id} itin={it} />
            ))}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
